import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { map, extend, chain } from 'underscore';
import { DefaultButton, Panel, PanelType, Stack } from '@fluentui/react';
import { IconButton } from '@bingads-webui-react/fabric-control';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { FluentImagePicker } from '@bingads-webui-campaign-react/fluent-image-picker';
import { panelStylesProp } from '@bingads-webui-campaign-react/asset-library-shared-add-media-layout';
import { CopilotTab, CopilotTabItem } from '@bingads-webui/inline-copilot-ui';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';

export const getImageSuggestionPanelStyles = () => ({
  panel: {
    top: 60,
  },
  panelHeader: {
    paddingLeft: 32,
    paddingRight: 24,
    alignSelf: 'justify-start',
    flexGrow: 1,
  },
  panelTitle: {
    fontSize: 20,
    fontWeight: 600,
    display: 'inline-block',
    lineHeight: '27px',
    paddingBottom: 20,
  },
  backButton: {
    display: 'inline-block',
    paddingRight: 10,
  },
  theme: {
    marginBottom: 14,
    marginLeft: 4,
  },
  buttonContainer: {
    alignItems: 'center',
  },
  errorMessage: {
    marginTop: 16,
  },
});

export const imageSuggestionPanelWidth = '920px';

export const ImageRecommendationsPanel = (props) => {
  const {
    i18n,
    title,
    images,
    themes,
    onSelectImage,
    onDismiss,
    loadNextPage,
    hasMore,
  } = props;

  const classes = useDefaultStyles(getImageSuggestionPanelStyles);
  const [activeImages, setActiveImages] = useState(images);
  const [activeTheme, setActiveTheme] = useState(i18n.getString(_TL_('All')));
  const themeNames = useMemo(() => [
    ...map(themes, ({ name }) => name),
  ], [themes]);

  const onSwitchTheme = useCallback((key) => {
    setActiveTheme(key);
    if (key === i18n.getString(_TL_('All'))) {
      setActiveImages(images);
    } else {
      const rawImages = chain(themes)
        .filter(theme => theme.name === key)
        .first()
        .result('images')
        .map(img => extend({}, img))
        .value();
      setActiveImages(rawImages);
    }
  }, [i18n, images, themes]);


  const onClickCancel = useCallback((e) => {
    onDismiss(e);
  }, [onDismiss]);

  const onRenderHeader = useCallback(() => (
    <div className={classes.panelHeader}>
      <div className={classes.backButton}>
        <IconButton iconProps={{ iconName: 'Back' }} className="back-button" onClick={onClickCancel} />
      </div>
      <div className={classes.panelTitle}>
        {i18n.getString(title)}
      </div>
    </div>
  ), [classes.backButton, classes.panelHeader, classes.panelTitle, i18n, onClickCancel, title]);

  const onRenderFooterContent = useCallback(() => (
    <Stack className={classnames('image-recommendations-panel-buttons', classes.buttonContainer)} horizontal tokens={{ childrenGap: 8 }}>
      <Stack.Item key="btn-cancel">
        <DefaultButton className="btn-cancel" onClick={onClickCancel}>
          {i18n.getString(_TL_('Cancel'))}
        </DefaultButton>
      </Stack.Item>

    </Stack>
  ), [classes.buttonContainer, i18n, onClickCancel]);

  const onSelectImageInternal = useCallback((index) => {
    const { fullUrl, suggestionId } = activeImages[index];
    onDismiss();
    onSelectImage(fullUrl, index, suggestionId);
  }, [activeImages, onDismiss, onSelectImage]);

  return (
    <Panel
      onRenderHeader={onRenderHeader}
      isOpen
      onDismiss={onClickCancel}
      onRenderFooterContent={onRenderFooterContent}
      isFooterAtBottom
      type={PanelType.custom}
      customWidth={imageSuggestionPanelWidth}
      styles={panelStylesProp}
      className="image-recommendations-panel"
    >
      <FluentProvider theme={webLightTheme}>
        <div className={classes.theme}>
          <CopilotTab
            active={activeTheme}
            onActiveChange={key => onSwitchTheme(key)}
          >
            <CopilotTabItem tabKey={i18n.getString(_TL_('All'))}>
              {i18n.getString(_TL_('All'))}
            </CopilotTabItem>
            {map(themeNames, name => (
              <CopilotTabItem tabKey={name}>
                {name}
              </CopilotTabItem>
              ))}
          </CopilotTab>
        </div>
      </FluentProvider>
      <FluentImagePicker
        images={activeImages}
        pageSize={30}
        loadMoreImages={loadNextPage}
        onSelectImage={onSelectImageInternal}
        hasMoreImages={hasMore}
        imageCellWidth={260}
        imageCellHeight={243}
      />
    </Panel>);
};

ImageRecommendationsPanel.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  title: PropTypes.string,
  images: PropTypes.arrayOf(PropTypes.shape({
    thumbnailSrc: PropTypes.string,
    fullUrl: PropTypes.string,
    suggestionId: PropTypes.string,
  })).isRequired,
  themes: PropTypes.arrayOf(PropTypes.shape({})),
  hasMore: PropTypes.bool.isRequired,
  loadNextPage: PropTypes.func.isRequired,
  onSelectImage: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

ImageRecommendationsPanel.defaultProps = {
  title: _TL_('Recommended images'),
  themes: [],
};
